import {
  type CSSProperties,
  type ReactNode,
  type RefObject,
  useRef,
} from "react";

import { cn } from "@btnet/utilities-core";

import { type BuiFontSize, BuiText } from "../BuiText/BuiText";
import "./BuiLabel.less";

export type BuiLabelAlign = "left" | "top";

/**
 * At least one of `text` or `ariaLabel` should be provided so the label has
 * either visible text or an accessible name for screen readers.
 */
export interface BuiLabelProps {
  /**
   * Id attribute for the label element.
   */
  id?: string;

  /**
   * data-testid attribute for the label element.
   */
  testid?: string;

  /**
   * Label text to display.
   */
  text?: string;

  /**
   * The alignment of the label relative to the source element.
   * @default 'left' when children present, 'top' when no children
   */
  align?: BuiLabelAlign;

  /**
   * The id of the input this label is associated with.
   */
  htmlFor?: string;

  /**
   * Additional classnames to apply to the label.
   */
  className?: string;

  /**
   * Accessible label text for screen readers (may be more descriptive than `text`).
   */
  ariaLabel?: string;

  /**
   * When true, adds a required indicator (*) after the label text.
   * @default false
   */
  isRequired?: boolean;

  /**
   * Inline styles for the label element.
   */
  style?: CSSProperties;

  /**
   * Font size of the label text.
   */
  size?: BuiFontSize;

  /**
   * Children rendered alongside the label (e.g., the associated input).
   */
  children?: ReactNode;

  /**
   * When true, renders the label text after the children instead of before.
   * @default false
   */
  labelAfter?: boolean;

  /**
   * Ref for the outer `<label>` element.
   */
  outerRef?: RefObject<HTMLLabelElement | null>;

  /**
   * Optional content rendered after the label text inside the content row (e.g. an info icon with a popover).
   * Prefer the `info` prop on `BTLabel` from `@btnet/core-components` over using this slot directly.
   */
  infoContent?: ReactNode;
}

/**
 * Blueprint label primitive. Prefer `BTLabel` from `@btnet/core-components` in app code.
 */
export function BuiLabel({
  id,
  testid,
  align,
  outerRef,
  text,
  className,
  ariaLabel,
  isRequired,
  size,
  style,
  children,
  htmlFor,
  labelAfter,
  infoContent,
}: BuiLabelProps) {
  const resolvedText = text ?? "";
  const outerR = useRef<HTMLLabelElement>(null);
  const resolvedOuterRef = outerRef ?? outerR;

  const adjustedSize = size ?? (labelAfter ? "normal-md" : "distinct-sm");

  let resolvedAlign = align;
  if (!children && !resolvedAlign) {
    resolvedAlign = "top";
  } else if (!resolvedAlign) {
    resolvedAlign = "left";
  }

  const label = (
    <div className={cn(resolvedText && "bui-label--content")}>
      <BuiText
        className="bui-label--text"
        text={resolvedText}
        tag="span"
        size={adjustedSize}
      />
      {infoContent}
    </div>
  );

  return (
    <label
      id={id ?? htmlFor}
      data-testid={testid}
      ref={resolvedOuterRef as RefObject<HTMLLabelElement>}
      htmlFor={htmlFor}
      className={cn("bui-label", className, {
        "bui-label--row": resolvedAlign === "left" && !labelAfter,
        "bui-label--col": resolvedAlign === "top" && children,
        "bui-label-required": isRequired,
      })}
      aria-label={ariaLabel}
      style={style}
    >
      {!labelAfter && label}
      {children}
      {labelAfter && label}
    </label>
  );
}
